import React, { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { AutorenewIcon } from "../../icons";
/** Store mínimo que el modal necesita (lo cumplen AutomationStore y el panel sim). */
export interface RetryModalStore {
  retryTarget: any | null;
  closeRetry: () => void;
  retryInfo: (id: string) => Promise<any>;
  doRetry: (id: string, mode: string, overrides?: Record<string, unknown>) => Promise<void>;
}

interface RetryInfo {
  canRetry: boolean;
  reason?: string | null;
  modes: string[];
  editableFields: string[];
  currentValues?: Record<string, unknown> | null;
}

export const RetryWorkerEventModal = observer(({ store }: { store: RetryModalStore }) => {
  const we = store.retryTarget;
  const [info, setInfo] = useState<RetryInfo | null>(null);
  const [mode, setMode] = useState<string>("stored");
  const [overrides, setOverrides] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!we) return;
    setInfo(null);
    setOverrides({});
    void store.retryInfo(we.id).then((i: RetryInfo) => {
      setInfo(i);
      setMode(i?.modes?.[0] ?? "stored");
    });
  }, [we, store]);

  const submit = async () => {
    setBusy(true);
    // Solo enviar los campos efectivamente editados.
    const changed: Record<string, unknown> = {};
    for (const f of info?.editableFields ?? []) {
      if (f in overrides) changed[f] = overrides[f];
    }
    await store.doRetry(we.id, mode, Object.keys(changed).length ? changed : undefined);
    setBusy(false);
  };

  return (
    <Dialog open={!!we} onClose={() => store.closeRetry()} maxWidth="sm" fullWidth>
      <DialogTitle>Destrabar — reintentar job</DialogTitle>
      <DialogContent>
        {we && (
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <Typography variant="body2" sx={{ fontFamily: "monospace" }} color="text.secondary">
              {we.jobType ?? we.displayId ?? we.id}
            </Typography>

            {!info ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={18} />
                <Typography variant="body2" color="text.secondary">
                  Cargando opciones de retry…
                </Typography>
              </Stack>
            ) : !info.canRetry ? (
              <Alert severity="error">No se puede reintentar: {info.reason ?? "—"}</Alert>
            ) : (
              <>
                <FormControl size="small" sx={{ maxWidth: 220 }}>
                  <InputLabel id="retry-mode-label">Modo</InputLabel>
                  <Select
                    labelId="retry-mode-label"
                    label="Modo"
                    value={mode}
                    onChange={(e) => setMode(e.target.value as string)}
                  >
                    {info.modes.map((m) => (
                      <MenuItem key={m} value={m}>
                        {m}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {info.editableFields.length > 0 && (
                  <Stack spacing={1.5}>
                    <Typography variant="h4">Modificar datos (opcional)</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Corregí el dato que causó el fallo; el reintento usará estos valores.
                    </Typography>
                    {info.editableFields.map((f) => {
                      const cur = (info.currentValues ?? {})[f];
                      const val = f in overrides ? overrides[f] : cur != null ? String(cur) : "";
                      return (
                        <TextField
                          key={f}
                          label={f}
                          size="small"
                          fullWidth
                          value={val}
                          onChange={(e) => setOverrides((o) => ({ ...o, [f]: e.target.value }))}
                        />
                      );
                    })}
                  </Stack>
                )}
              </>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => store.closeRetry()} disabled={busy}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          startIcon={<AutorenewIcon />}
          onClick={() => void submit()}
          disabled={busy || !info?.canRetry}
        >
          {busy ? "Reintentando…" : "Reintentar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
});
